import React, { useEffect, useState, useCallback } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, IconButton, Badge, Tooltip, Box } from '@mui/material';
import chestImg from '../../assets/img/d2rchest.png';
import GenericBubble from './GenericBubble';

// Import content components
import HelpBubbleContent from '../HelpBubble/HelpBubbleContent';
import MuleBubbleContent from '../MuleBubble/MuleBubbleContent';
import TrackerBubbleContent from '../TrackerBubble/TrackerBubbleContent';
import FiltersBubbleContent from '../FiltersBubble/FiltersBubbleContent';
import CartBubbleContent from '../CartBubble/CartBubbleContent';
import OrderBubbleContent from '../OrderBubble/OrderBubbleContent';
import { useTrackedItems } from '../../stores';
import { useTrackedProducts } from '../../stores/trackedProducts';
import { FilterAltOutlined, FilterAltOffOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { clearFilterValues, toggleFilterBubble } from '../../features/filters/filterSlice';
import { RootState } from '../../stores/store';
import { useTranslation } from 'react-i18next';
import { useCartItems } from '../../stores/cartItems';
import { useFilteredOrders, notifyAboutOpenOrders } from '../../stores/orderItems';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../config/config.json';
import { isStaffOrderPage } from '../../utils/orderUtils';
import { useHeaderLinks } from 'components/Header/HeaderLinksContext';

interface BubbleContainerProps {
  pageKey?: string;
  helpText?: string;
  hideHelpBubble?: boolean;
  hideMuleBubble?: boolean;
  hideTrackerBubble?: boolean;
  hideFiltersBubble?: boolean;
  hideCartBubble?: boolean;
  hideOrderBubble?: boolean;
}

// Base z-index values for bubble containers
const BASE_CONTAINER_Z_INDEX = 10000;
// Z-index increment for active bubbles
const ACTIVE_Z_INDEX_INCREMENT = 1000;

// Bubble type IDs
const BUBBLE_IDS = {
  help: 'help-bubble',
  mule: 'mule-bubble',
  tracker: 'tracker-bubble',
  filters: 'filters-bubble',
  cart: 'cart-bubble',
  order: 'order-bubble',
};

export default function BubbleContainer({
  pageKey,
  helpText,
  hideHelpBubble = false,
  hideMuleBubble = false,
  hideTrackerBubble = false,
  hideFiltersBubble = false,
  hideCartBubble = false,
  hideOrderBubble = false,
}: BubbleContainerProps) {
  // State for each bubble
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isMuleOpen, setIsMuleOpen] = useState(false);
  const [isTrackerOpen, setIsTrackerOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // State to track the z-index of each bubble container
  const [containerZIndices, setContainerZIndices] = useState({
    [BUBBLE_IDS.help]: BASE_CONTAINER_Z_INDEX,
    [BUBBLE_IDS.mule]: BASE_CONTAINER_Z_INDEX,
    [BUBBLE_IDS.tracker]: BASE_CONTAINER_Z_INDEX,
    [BUBBLE_IDS.filters]: BASE_CONTAINER_Z_INDEX,
    [BUBBLE_IDS.cart]: BASE_CONTAINER_Z_INDEX,
    [BUBBLE_IDS.order]: BASE_CONTAINER_Z_INDEX,
  });

  // Track last active container
  const [lastActiveContainer, setLastActiveContainer] = useState<string | null>(null);

  // Get tracked items and products count
  const { data: trackedItems = [] } = useTrackedItems();
  const { data: trackedProducts = [] } = useTrackedProducts();
  const totalTrackedItems = trackedItems.length + trackedProducts.length;

  // Get cart items count
  const { data: cartItems = [] } = useCartItems();

  // Query for mule games to get the count for the badge
  const sessionToken = localStorage.getItem('session_token');
  const { data: muleGames = [] } = useQuery({
    queryKey: ['muleGames'],
    queryFn: async () => {
      if (!sessionToken) return [];
      const response = await axios.get(`${config.backend_base_url}mygames`, {
        headers: { 'session-token': sessionToken },
      });
      return response.data || [];
    },
    enabled:
      !!sessionToken &&
      !hideMuleBubble &&
      parseInt(localStorage.getItem('show_mule_bubble') || '0', 10) === 1, // Only fetch when mule bubble should be visible
  });

  // Get orders count
  const { ongoingOrdersCount, isLoading: isOrdersLoading } = useFilteredOrders();

  // Check if user is staff
  const { isD2RS } = useHeaderLinks();

  // Check if current page is staff order page
  const currentIsStaffOrderPage = isStaffOrderPage();

  // Get filter states from Redux for the FilterBubble
  const {
    newItems,
    ethereal,
    unidentified,
    limitFilter,
    rarity,
    type,
    itemType,
    viewType,
    statFilterGroups,
    isFilterBubbleOpen,
  } = useSelector((state: RootState) => state.filters);

  // Update container z-indices when a bubble becomes active
  const updateLastActiveContainer = useCallback(
    (containerId: string) => {
      if (lastActiveContainer === containerId) return;

      setLastActiveContainer(containerId);

      // Update z-indices to bring the active container to the front
      setContainerZIndices((prev) => {
        const newIndices = { ...prev };
        // Set a higher z-index for the active container
        const maxZIndex = Math.max(...Object.values(prev)) + ACTIVE_Z_INDEX_INCREMENT;
        newIndices[containerId] = maxZIndex;
        return newIndices;
      });
    },
    [lastActiveContainer]
  );

  // Show toast notification for open orders when orders are loaded
  useEffect(() => {
    if (!isOrdersLoading && ongoingOrdersCount > 0 && isD2RS && !currentIsStaffOrderPage) {
      notifyAboutOpenOrders(ongoingOrdersCount);
    }
  }, [isOrdersLoading, ongoingOrdersCount, isD2RS, currentIsStaffOrderPage]);

  // Sync the Redux isFilterBubbleOpen state with the local isFiltersOpen state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setIsFiltersOpen(isFilterBubbleOpen);
    if (isFilterBubbleOpen) {
      updateLastActiveContainer(BUBBLE_IDS.filters);
    }
  }, [isFilterBubbleOpen, updateLastActiveContainer]);

  // Function to count active filters - copied from FiltersBubbleContent
  const countActiveFilters = () => {
    let count = 0;
    if (rarity !== '') count++;
    if (type !== '') count++;
    if (itemType !== '') count++;
    // Count all stat filters across all groups
    statFilterGroups?.forEach((group) => {
      if (group?.filters?.length > 0) count++;
    });
    if (unidentified !== '') count++;
    if (ethereal !== '') count++;
    if (newItems !== '') count++;
    if (limitFilter !== 100) count++;
    if (viewType !== 'all') count++;
    return count;
  };

  // Clear filters function for the reset badge
  const handleClearFilters = () => {
    dispatch(clearFilterValues());
  };

  // Initialize localStorage values if not set
  useEffect(() => {
    if (localStorage.getItem('show_help_bubble') === null) {
      localStorage.setItem('show_help_bubble', '1');
    }
    if (localStorage.getItem('show_mule_bubble') === null) {
      localStorage.setItem('show_mule_bubble', '0');
    }
    if (localStorage.getItem('show_tracker_bubble') === null) {
      localStorage.setItem('show_tracker_bubble', '1');
    }
    if (localStorage.getItem('show_filter_bubble') === null) {
      localStorage.setItem('show_filter_bubble', '1');
    }
    if (localStorage.getItem('show_cart_bubble') === null) {
      localStorage.setItem('show_cart_bubble', '1');
    }
    if (localStorage.getItem('show_order_bubble') === null) {
      localStorage.setItem('show_order_bubble', '1');
    }
  }, []);

  // Check if bubbles should be shown based on localStorage and props
  const showHelpBubble =
    !hideHelpBubble && parseInt(localStorage.getItem('show_help_bubble') || '1', 10) === 1;
  const showMuleBubble =
    !hideMuleBubble &&
    parseInt(localStorage.getItem('show_mule_bubble') || '1', 10) === 1 &&
    localStorage.getItem('session_token') !== null;
  const showTrackerBubble =
    !hideTrackerBubble && parseInt(localStorage.getItem('show_tracker_bubble') || '1', 10) === 1;
  const showFiltersBubble =
    !hideFiltersBubble && parseInt(localStorage.getItem('show_filter_bubble') || '1', 10) === 1;
  const showCartBubble =
    !hideCartBubble &&
    parseInt(localStorage.getItem('show_cart_bubble') || '1', 10) === 1 &&
    localStorage.getItem('session_token') !== null;
  const showOrderBubble =
    !hideOrderBubble &&
    parseInt(localStorage.getItem('show_order_bubble') || '1', 10) === 1 &&
    isD2RS &&
    !currentIsStaffOrderPage &&
    localStorage.getItem('session_token') !== null;

  // Calculate positions for the bubbles
  const visibleBubbles = [
    showMuleBubble && 'mule', // Mule bubble below tracker
    showFiltersBubble && 'filters', // Filter bubble at the top
    showTrackerBubble && 'tracker', // Tracker bubble below filter
    showOrderBubble && 'order', // Order bubble in the 3rd position if applicable
    showCartBubble && 'cart', // Cart bubble
    showHelpBubble && 'help', // Help is positioned at the end to appear at the bottom
  ].filter(Boolean) as string[];

  // Get position for each bubble
  const getBubblePosition = (bubbleType: string) => {
    const index = visibleBubbles.indexOf(bubbleType);
    if (index === -1) return 0;
    // For help bubble, we want it at the bottom (position 0)
    if (bubbleType === 'help') return 0;
    // For other bubbles, maintain their positions, but shifted up by 1 to make room for help at bottom
    return index + 1;
  };

  // Update bubble state and bring container to front
  const closeAllBubbles = () => {
    setIsHelpOpen(false);
    setIsMuleOpen(false);
    setIsTrackerOpen(false);
    setIsOrderOpen(false);
    // For filters bubble, we need to also update Redux state if it's open
    if (isFiltersOpen) {
      dispatch(toggleFilterBubble());
    }
    setIsFiltersOpen(false);
    setIsCartOpen(false);
  };

  const setHelpOpenWithFocus = (isOpen: boolean) => {
    setIsHelpOpen(isOpen);
    if (isOpen) {
      updateLastActiveContainer(BUBBLE_IDS.help);
    }
  };

  const setMuleOpenWithFocus = (isOpen: boolean) => {
    setIsMuleOpen(isOpen);
    if (isOpen) {
      updateLastActiveContainer(BUBBLE_IDS.mule);
    }
  };

  const setTrackerOpenWithFocus = (isOpen: boolean) => {
    setIsTrackerOpen(isOpen);
    if (isOpen) {
      updateLastActiveContainer(BUBBLE_IDS.tracker);
    }
  };

  const setFiltersOpenWithFocus = (isOpen: boolean) => {
    setIsFiltersOpen(isOpen);
    // Also update Redux state when the filter bubble is opened/closed directly
    if (isOpen !== isFilterBubbleOpen) {
      dispatch(toggleFilterBubble());
    }
    if (isOpen) {
      updateLastActiveContainer(BUBBLE_IDS.filters);
    }
  };

  const setCartOpenWithFocus = (isOpen: boolean) => {
    setIsCartOpen(isOpen);
    if (isOpen) {
      updateLastActiveContainer(BUBBLE_IDS.cart);
    }
  };

  const setOrderOpenWithFocus = (isOpen: boolean) => {
    if (isOpen) {
      closeAllBubbles();
      setIsOrderOpen(true);
      updateLastActiveContainer(BUBBLE_IDS.order);
    } else {
      setIsOrderOpen(false);
    }
  };

  // Create header components for each bubble
  const helpHeader = (
    <>
      <Typography variant="h6" sx={{ textTransform: 'none' }}>
        Help
      </Typography>
      <IconButton size="small" onClick={() => setHelpOpenWithFocus(false)} aria-label="close help">
        <CloseIcon />
      </IconButton>
    </>
  );

  const muleHeader = (
    <>
      <Typography variant="h6" sx={{ textTransform: 'none' }}>
        Mule Games
      </Typography>
      <IconButton
        size="small"
        onClick={() => setMuleOpenWithFocus(false)}
        aria-label="close mule games"
      >
        <CloseIcon />
      </IconButton>
    </>
  );

  const trackerHeader = (
    <>
      <Typography variant="h6" sx={{ textTransform: 'none' }}>
        Tracked Items
      </Typography>
      <IconButton
        size="small"
        onClick={() => setTrackerOpenWithFocus(false)}
        aria-label="close tracker"
      >
        <CloseIcon />
      </IconButton>
    </>
  );

  const filtersHeader = (
    <>
      <Typography variant="h6" sx={{ textTransform: 'none' }}>
        Filters
      </Typography>
      <IconButton
        size="small"
        onClick={() => setFiltersOpenWithFocus(false)}
        aria-label="close filters"
      >
        <CloseIcon />
      </IconButton>
    </>
  );

  const cartHeader = (
    <>
      <Typography variant="h6" sx={{ textTransform: 'none' }}>
        Cart
      </Typography>
      <IconButton size="small" onClick={() => setCartOpenWithFocus(false)} aria-label="close cart">
        <CloseIcon />
      </IconButton>
    </>
  );

  const orderHeader = (
    <>
      <Typography variant="h6" sx={{ textTransform: 'none' }}>
        Orders
      </Typography>
      <IconButton
        size="small"
        onClick={() => setOrderOpenWithFocus(false)}
        aria-label="close orders"
      >
        <CloseIcon />
      </IconButton>
    </>
  );

  // Create a custom mule icon with badge styling similar to filter badge
  const muleIconWithBadge = (
    <Box sx={{ position: 'relative' }}>
      <Badge
        badgeContent={muleGames.length}
        color="primary"
        invisible={isMuleOpen || muleGames.length <= 0}
        overlap="circular"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiBadge-badge': {
            right: '-4px',
            top: '-4px',
            minWidth: '20px',
            height: '20px',
            padding: '0 6px',
            fontSize: '0.75rem',
            border: 'none',
          },
        }}
      >
        <img style={{ width: '32px', verticalAlign: 'middle' }} src={chestImg} alt="Mule Games" />
      </Badge>
    </Box>
  );

  // Create a custom BookmarkBorderIcon with badge styling similar to filter badge
  const trackerIconWithBadge = (
    <Box sx={{ position: 'relative' }}>
      <Badge
        badgeContent={totalTrackedItems}
        color="primary"
        invisible={isTrackerOpen || totalTrackedItems <= 0}
        overlap="circular"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiBadge-badge': {
            right: '-4px',
            top: '-4px',
            minWidth: '20px',
            height: '20px',
            padding: '0 6px',
            fontSize: '0.75rem',
            border: 'none',
          },
        }}
      >
        {isTrackerOpen ? <BookmarkIcon /> : <BookmarkBorderIcon />}
      </Badge>
    </Box>
  );

  // Create cart icon with badge
  const cartIconWithBadge = (
    <Box sx={{ position: 'relative' }}>
      <Badge
        badgeContent={cartItems.length}
        color="primary"
        invisible={isCartOpen || cartItems.length <= 0}
        overlap="circular"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiBadge-badge': {
            right: '-4px',
            top: '-4px',
            minWidth: '20px',
            height: '20px',
            padding: '0 6px',
            fontSize: '0.75rem',
            border: 'none',
          },
        }}
      >
        <ShoppingCartIcon />
      </Badge>
    </Box>
  );

  // Create order icon with badge
  const orderIconWithBadge = (
    <Box sx={{ position: 'relative' }}>
      <Badge
        badgeContent={ongoingOrdersCount}
        color="primary"
        invisible={isOrderOpen || ongoingOrdersCount <= 0}
        overlap="circular"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiBadge-badge': {
            right: '-4px',
            top: '-4px',
            minWidth: '20px',
            height: '20px',
            padding: '0 6px',
            fontSize: '0.75rem',
            border: 'none',
          },
        }}
      >
        <AssignmentOutlinedIcon />
      </Badge>
    </Box>
  );

  // Footer spacing value to use for all bubbles
  const footerSpacing = 120;

  // Create a custom FilterAltOutlined icon with clear filters badge
  const filterIconWithClearBadge = (
    <Box sx={{ position: 'relative' }}>
      {/* Left-side clear filter badge */}
      <Badge
        badgeContent={
          <Tooltip title={t('filters.clearTooltip', 'Clear all filters')} placement="top" arrow>
            <Box
              sx={{
                bgcolor: 'error.main',
                borderRadius: '50%',
                padding: '2px',
                display: 'flex',
                cursor: 'pointer',
                width: '20px',
                height: '20px',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleClearFilters();
              }}
            >
              <FilterAltOffOutlined sx={{ fontSize: 16, width: 16, height: 16, color: 'white' }} />
            </Box>
          </Tooltip>
        }
        invisible={isFiltersOpen || countActiveFilters() === 0}
        overlap="circular"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiBadge-badge': {
            left: '-20px',
            top: '-4px',
            border: 'none',
          },
        }}
      >
        <FilterAltOutlined />
      </Badge>
    </Box>
  );

  // Add cart event listener for real-time badge updates
  const queryClient = useQueryClient();
  useEffect(() => {
    const handleCartItemAdded = () => {
      // Invalidate the cart items query to update the badge
      queryClient.invalidateQueries({ queryKey: ['cartItems'] });
    };

    const handleMuleGamesUpdated = () => {
      // Invalidate the mule games query to update the badge
      queryClient.invalidateQueries({ queryKey: ['muleGames'] });
    };

    const handleOrderStatusUpdated = () => {
      // Invalidate the orders query to update the badge
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    };

    window.addEventListener('cartItemAdded', handleCartItemAdded);
    window.addEventListener('cartItemsRemoved', handleCartItemAdded);
    window.addEventListener('orderPlaced', handleCartItemAdded);
    window.addEventListener('orderStatusUpdated', handleOrderStatusUpdated);

    // Listen for mule game events
    window.addEventListener('muleGameOpened', handleMuleGamesUpdated);
    window.addEventListener('muleGamesClosed', handleMuleGamesUpdated);
    window.addEventListener('muleGamesUpdated', handleMuleGamesUpdated);

    return () => {
      window.removeEventListener('cartItemAdded', handleCartItemAdded);
      window.removeEventListener('cartItemsRemoved', handleCartItemAdded);
      window.removeEventListener('orderPlaced', handleCartItemAdded);
      window.removeEventListener('muleGameOpened', handleMuleGamesUpdated);
      window.removeEventListener('muleGamesClosed', handleMuleGamesUpdated);
      window.removeEventListener('muleGamesUpdated', handleMuleGamesUpdated);
      window.removeEventListener('orderStatusUpdated', handleOrderStatusUpdated);
    };
  }, [queryClient]);

  // If no bubbles are visible, don't render anything
  if (
    !showHelpBubble &&
    !showMuleBubble &&
    !showTrackerBubble &&
    !showFiltersBubble &&
    !showCartBubble &&
    !showOrderBubble
  ) {
    return null;
  }

  return (
    <>
      {showFiltersBubble && (
        <GenericBubble
          isOpen={isFiltersOpen}
          setIsOpen={setFiltersOpenWithFocus}
          icon={
            <Badge
              badgeContent={countActiveFilters()}
              color="primary"
              invisible={isFiltersOpen || countActiveFilters() === 0}
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{
                '& .MuiBadge-badge': {
                  right: '-4px',
                  top: '-4px',
                  minWidth: '20px',
                  height: '20px',
                  padding: '0 6px',
                  fontSize: '0.75rem',
                },
              }}
            >
              {filterIconWithClearBadge}
            </Badge>
          }
          headerContent={filtersHeader}
          mainContent={<FiltersBubbleContent />}
          position={getBubblePosition('filters')}
          footerSpacing={footerSpacing}
          containerId={BUBBLE_IDS.filters}
          containerZIndex={containerZIndices[BUBBLE_IDS.filters]}
          updateLastActiveContainer={updateLastActiveContainer}
        />
      )}

      {showTrackerBubble && (
        <GenericBubble
          isOpen={isTrackerOpen}
          setIsOpen={setTrackerOpenWithFocus}
          icon={trackerIconWithBadge}
          headerContent={trackerHeader}
          mainContent={<TrackerBubbleContent />}
          position={getBubblePosition('tracker')}
          footerSpacing={footerSpacing}
          containerId={BUBBLE_IDS.tracker}
          containerZIndex={containerZIndices[BUBBLE_IDS.tracker]}
          updateLastActiveContainer={updateLastActiveContainer}
        />
      )}

      {showOrderBubble && (
        <GenericBubble
          isOpen={isOrderOpen}
          setIsOpen={setOrderOpenWithFocus}
          icon={orderIconWithBadge}
          headerContent={orderHeader}
          mainContent={<OrderBubbleContent />}
          position={getBubblePosition('order')}
          footerSpacing={footerSpacing}
          containerId={BUBBLE_IDS.order}
          containerZIndex={containerZIndices[BUBBLE_IDS.order]}
          updateLastActiveContainer={updateLastActiveContainer}
        />
      )}

      {showMuleBubble && (
        <GenericBubble
          isOpen={isMuleOpen}
          setIsOpen={setMuleOpenWithFocus}
          icon={muleIconWithBadge}
          headerContent={muleHeader}
          mainContent={<MuleBubbleContent />}
          position={getBubblePosition('mule')}
          footerSpacing={footerSpacing}
          containerId={BUBBLE_IDS.mule}
          containerZIndex={containerZIndices[BUBBLE_IDS.mule]}
          updateLastActiveContainer={updateLastActiveContainer}
        />
      )}

      {showCartBubble && (
        <GenericBubble
          isOpen={isCartOpen}
          setIsOpen={setCartOpenWithFocus}
          icon={cartIconWithBadge}
          headerContent={cartHeader}
          mainContent={<CartBubbleContent />}
          position={getBubblePosition('cart')}
          footerSpacing={footerSpacing}
          containerId={BUBBLE_IDS.cart}
          containerZIndex={containerZIndices[BUBBLE_IDS.cart]}
          updateLastActiveContainer={updateLastActiveContainer}
        />
      )}

      {showHelpBubble && (
        <GenericBubble
          isOpen={isHelpOpen}
          setIsOpen={setHelpOpenWithFocus}
          icon={<HelpOutlineIcon />}
          headerContent={helpHeader}
          mainContent={<HelpBubbleContent pageKey={pageKey} defaultText={helpText} />}
          position={getBubblePosition('help')}
          footerSpacing={footerSpacing}
          containerId={BUBBLE_IDS.help}
          containerZIndex={containerZIndices[BUBBLE_IDS.help]}
          updateLastActiveContainer={updateLastActiveContainer}
        />
      )}
    </>
  );
}
